import { KNOWN_CLIENT_ID, CLIENT_INFO_STORAGE } from "./constants.js";
import { InternalUUIDMetricType as UUIDMetricType } from "./metrics/types/uuid.js";
import { InternalDatetimeMetricType as DatetimeMetricType } from "./metrics/types/datetime.js";
import { InternalStringMetricType as StringMetricType } from "./metrics/types/string.js";
import { InternalCounterMetricType as CounterMetricType } from "./metrics/types/counter.js";
import { createMetric } from "./metrics/utils.js";
import TimeUnit from "./metrics/time_unit.js";
import { generateUUIDv4, isWindowObjectUnavailable } from "./utils.js";
import log, { LoggingLevel } from "./log.js";
import { Context } from "./context.js";
import { isSessionInactive } from "./sessions.js";
const LOG_TAG = "core.InternalMetrics";
export class CoreMetrics {
    constructor() {
        this.clientId = new UUIDMetricType({
            name: "client_id",
            category: "",
            sendInPings: ["glean_client_info"],
            lifetime: "user",
            disabled: false
        });
        this.firstRunDate = new DatetimeMetricType({
            name: "first_run_date",
            category: "",
            sendInPings: ["glean_client_info"],
            lifetime: "user",
            disabled: false
        }, TimeUnit.Day);
        this.os = new StringMetricType({
            name: "os",
            category: "",
            sendInPings: ["glean_client_info"],
            lifetime: "application",
            disabled: false
        });
        this.osVersion = new StringMetricType({
            name: "os_version",
            category: "",
            sendInPings: ["glean_client_info"],
            lifetime: "application",
            disabled: false
        });
        this.architecture = new StringMetricType({
            name: "architecture",
            category: "",
            sendInPings: ["glean_client_info"],
            lifetime: "application",
            disabled: false
        });
        this.locale = new StringMetricType({
            name: "locale",
            category: "",
            sendInPings: ["glean_client_info"],
            lifetime: "application",
            disabled: false
        });
        this.appChannel = new StringMetricType({
            name: "app_channel",
            category: "",
            sendInPings: ["glean_client_info"],
            lifetime: "application",
            disabled: false
        });
        this.appBuild = new StringMetricType({
            name: "app_build",
            category: "",
            sendInPings: ["glean_client_info"],
            lifetime: "application",
            disabled: false
        });
        this.appDisplayVersion = new StringMetricType({
            name: "app_display_version",
            category: "",
            sendInPings: ["glean_client_info"],
            lifetime: "application",
            disabled: false
        });
        this.buildDate = new DatetimeMetricType({
            name: "build_date",
            category: "",
            sendInPings: ["glean_client_info"],
            lifetime: "application",
            disabled: false
        }, "second");
        this.sessionId = new UUIDMetricType({
            name: "session_id",
            category: "",
            sendInPings: ["glean_client_info"],
            lifetime: "user",
            disabled: false
        });
        this.sessionCount = new CounterMetricType({
            name: "session_count",
            category: "",
            sendInPings: ["glean_client_info"],
            lifetime: "user",
            disabled: false
        });
    }
    initialize() {
        if (!Context.testing && isWindowObjectUnavailable()) {
            return;
        }
        this.initializeClientId();
        this.initializeFirstRunDate();
        this.updateSessionInfo();
        this.os.set(Context.platform.info.os());
        this.osVersion.set(Context.platform.info.osVersion());
        this.architecture.set(Context.platform.info.arch());
        this.locale.set(Context.platform.info.locale());
        this.appBuild.set(Context.config.appBuild || "Unknown");
        this.appDisplayVersion.set(Context.config.appDisplayVersion || "Unknown");
        if (Context.config.channel) {
            this.appChannel.set(Context.config.channel);
        }
        if (Context.config.buildDate) {
            this.buildDate.set();
        }
    }
    updateSessionInfo() {
        if (isWindowObjectUnavailable()) {
            return;
        }
        const existingSessionId = Context.metricsDatabase.getMetric(CLIENT_INFO_STORAGE, this.sessionId);
        if (existingSessionId) {
            try {
                if (isSessionInactive(Context.config.sessionLengthInMinutesOverride)) {
                    this.generateNewSession();
                }
            }
            catch (e) {
                this.generateNewSession();
            }
        }
        else {
            this.generateNewSession();
        }
        localStorage.setItem("glean_session_last_active", Date.now().toString());
    }
    initializeClientId() {
        let needNewClientId = false;
        const clientIdData = Context.metricsDatabase.getMetric(CLIENT_INFO_STORAGE, this.clientId);
        if (clientIdData) {
            try {
                const currentClientId = createMetric("uuid", clientIdData);
                if (currentClientId.payload() === KNOWN_CLIENT_ID) {
                    needNewClientId = true;
                }
            }
            catch (_a) {
                log(LOG_TAG, "Unexpected value found for Glean clientId. Ignoring.", LoggingLevel.Warn);
                needNewClientId = true;
            }
        }
        else {
            needNewClientId = true;
        }
        if (needNewClientId) {
            this.clientId.set(generateUUIDv4());
        }
    }
    initializeFirstRunDate() {
        const firstRunDate = Context.metricsDatabase.getMetric(CLIENT_INFO_STORAGE, this.firstRunDate);
        if (!firstRunDate) {
            this.firstRunDate.set();
        }
    }
    generateNewSession() {
        this.sessionId.generateAndSet();
        this.sessionCount.add();
    }
}
